import { defineComponent as _defineComponent } from 'vue'
import { withDefaults, watch, ref, computed, getCurrentInstance, onMounted } from 'vue';
import { ISeller, IBuyer, IBuyPackage, IFastBuyPackageList, IPreference, IOrganization } from '@/Interfaces';
import { IPackageGroup } from './IPackageGroup';
import { AjaxHelper, IPaginatedList, AjaxHelperException } from '@samc/common';

import { Field, DropdownField, Action, Validations } from '@samc/centricity-core';
import { BuyerSource, PackageSource, SellerSource } from '@/Sources';

import { BuyerFormModal, SellerFormModal, PackageFormModal } from '@/Components/Modals';
import Entitlements from '@/Entitlements';
import { isNullOrEmpty } from '../../utils';

import { useUser } from '@/useUser';


export default /*#__PURE__*/_defineComponent({
  __name: 'PackageSelector',
  props: {
    addBuyer: { type: Boolean, required: true, default: false },
    addSeller: { type: Boolean, required: true, default: false },
    addPackage: { type: Boolean, required: true, default: false },
    verificationToken: { type: String, required: true },
    status: { type: String, required: true, default: 'Bid|Won|Lost|Indication|Funded|Pending Settle|Passed' },
    hasLoans: { type: [Boolean, null], required: true, default: () => null },
    value: { type: null, required: true },
    fieldClass: { type: [String, Array, Object], required: true },
    getDefaultPackageNameCallback: { type: Function, required: false },
    validations: { type: null, required: true, default: () => null },
    clearable: { type: Boolean, required: true, default: false },
    loadPreferences: { type: Boolean, required: true, default: false },
    disabled: { type: Boolean, required: true, default: false }
  },
  emits: ["input", "buyer", "seller", "package"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'input', value: IPackageGroup): void,
  (event: 'buyer', value: IBuyer | null): void,
  (event: 'seller', value: ISeller | null): void,
  (event: 'package', value: IFastBuyPackageList | null): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { addBuyer: boolean, addSeller: boolean, addPackage: boolean, verificationToken: string, status: string, hasLoans: boolean | null, value: IPackageGroup, fieldClass: string | string[] | Record<string, boolean>, getDefaultPackageNameCallback?: (prefix: string) => string, validations: Validations<IPackageGroup> | null, clearable: boolean, loadPreferences: boolean, disabled: boolean };

const instance = getCurrentInstance();





const dataSourceUrl = ref('/datasource');
const helper = ref<AjaxHelper | null>(null);
const model = ref<IPackageGroup>({
  buyerId: null,
  sellerId: null,
  packageId: null
});

const buyerSource = ref(new BuyerSource(true));
const sellerSource = ref(new SellerSource(true));
const packageSource = ref(new PackageSource());
const entitledToEditCounterparty = ref(false);

const buyerLoading = computed((): boolean => !!model.value.buyerId && !buyerSource.value.isLoaded);
const sellerLoading = computed((): boolean => !!model.value.sellerId && !sellerSource.value.isLoaded);
const packageLoading = computed((): boolean => !!model.value.packageId && packageSource.value.items.length === 0);

const { user } = useUser();

const clearPackage = () => {
  model.value.packageId = null;
}

const addNewBuyer = async () => {
  let newOrganization = await instance?.proxy.$modal<IOrganization | undefined>({ component: BuyerFormModal, title: 'Add New Counterparty', width: 450 });
  if (helper.value !== null && user.value !== null && newOrganization !== undefined) {
    try {
      window.loading?.('Creating Counterparty');
      newOrganization.createdBy = user.value.email;
      const buyer = await helper.value.postData<IBuyer>('buyers', newOrganization);
      if (!Array.isArray(buyerSource.value)) {
        buyerSource.value.clientItems.push(buyer);
      }
      buyerSource.value = new BuyerSource(true);
      model.value.buyerId = buyer.id;
    }catch (e: any) {
      instance?.proxy.$log('Failed Adding New Counterparty', e);
      if (e instanceof AjaxHelperException) {
        // TODO: This needs to be updated when we change error logging changes
        window.showAlert?.error(`Counterparty name: ${e?.data ?? e.message}`);
      } else {
        window.showAlert?.error(`An error occurred`);
      }
    } finally {
      window.loading?.();
    }
  }
}

const addNewSeller = async () => {
  let newOrganization = await instance?.proxy.$modal<IOrganization | undefined>({ component: SellerFormModal, title: 'Add New Counterparty', width: 450 });
  if (helper.value !== null && newOrganization !== undefined) {
    try {
      window.loading?.('Creating Counterparty');
      newOrganization.createdBy = user.value?.email;
      const seller = await helper.value.postData<ISeller>('sellers', newOrganization);
      if (!Array.isArray(sellerSource.value)) {
        sellerSource.value.clientItems.push(seller);
      }
      sellerSource.value = new SellerSource(true);
      model.value.sellerId = seller.id;
    }catch (e: any) {
      instance?.proxy.$log('Failed Adding New Counterparty', e);
      if (e instanceof AjaxHelperException) {
        // TODO: This needs to be updated when we change error logging changes
        window.showAlert?.error(`Counterparty name: ${e?.data || e.message}`);
      } else {
        window.showAlert?.error(`An error occurred`);
      }
    } finally {
      window.loading?.();
    }
  }
}

const addNewPackage = async () => {
  if (model.value.sellerId === null) {
    window.showAlert?.warning(`No seller is selected`);
    return;
  }
  const sellerMoniker = await getSellerMoniker(model.value.sellerId);
  const pkg = await instance?.proxy.$modal<IBuyPackage>({
    component: PackageFormModal,
    title: 'Add New Package',
    width: 850,
    props: {
      sellerMoniker: sellerMoniker,
      getDefaultPackageNameCallback: props.getDefaultPackageNameCallback
    }
  });
  if (helper.value !== null && pkg !== undefined && model.value.sellerId !== null && model.value.buyerId !== null) {
    try {
      window.loading?.('Creating Package');
      pkg.packageBuyInfo.seller = { id: model.value.sellerId, name: '', associatedInternalEntity: '' };
      pkg.packageBuyInfo.buyer = { id: model.value.buyerId, name: '' };

      const newPkg = await helper.value.postData<IBuyPackage>('buypackage', pkg);
      if (!Array.isArray(packageSource.value)) {
        packageSource.value.clientItems.push({
          packageId: newPkg.internalId ?? -1,
          id: newPkg.id ?? '',
          packageName: newPkg.name ?? '',
          alias: newPkg.alias,
          packageStatus: newPkg.status,
          buyerId: model.value.buyerId,
          buyerName: newPkg.packageBuyInfo.buyer?.name ?? '',
          sellerId: model.value.sellerId,
          sellerName: newPkg.packageBuyInfo.seller?.name ?? '',
          latestVersionId: null,
          hasLoans: false,
          bidRequestDate: newPkg.packageBuyInfo.bidRequestDate,
          bidDueDate: newPkg.packageBuyInfo.bidDueDate,
          assetClassTypeName: newPkg.assetClass.name,
          balanceTypeName: newPkg.packageBuyInfo.balanceType.name,
          currencyName: newPkg.currency.name,
          countryName: newPkg.country.name,
          loanLevelPricingIndicator: newPkg.packageBuyInfo.loanLevelPricingIndicator
        });
      }
      model.value.packageId = newPkg.id ?? null;
      packageSource.value = new PackageSource(model.value.sellerId, model.value.buyerId, props.hasLoans, props.status, model.value.packageId);        
    }catch (e: any) {
      instance?.proxy.$log('Failed Adding New Package', e);
      if (e instanceof AjaxHelperException && e.response.status === 400) {
        // TODO: This needs to be updated when we change error logging changes
        // Try to get the errors from the response
       const errors = (e?.data as { errors: { message:string }[] })?.errors;
       if(errors && errors.length === 1) {
          window.showAlert?.error('Failed to add new package.', errors[0].message);
        } else {
          window.showAlert?.error('Failed to add new package.', undefined, false, e);
        }
      } else {
        window.showAlert?.error(`An error occurred`);
      }
    } finally {
      window.loading?.();
    }
  }
}

const getDefaultBuyer = async () => {
  const key = 'DefaultBuyer';
  const defBuyer = await helper.value?.getData<IPreference>('userpreference', { key });
  if (!defBuyer) {
    return '';
  }
  return defBuyer.value.toLowerCase();
}
//#endregion

const packageDataLoaded = async () => {
  if (model.value.packageId !== null) {
    await setPackage(model.value.packageId);
  }
}

const setPackage = async (packageId: string | null) => {
  let pkg: IFastBuyPackageList | null = null;
  if (!isNullOrEmpty(packageId)) {
    if (packageSource.value.items.length > 0) {
      pkg = packageSource.value.items.find(x => x.id === packageId) ?? null;
    }
    if (pkg === null) {
      const helper = new AjaxHelper('/datasource/packages', props.verificationToken, null, null);
      const pkgs = await helper.getData<IPaginatedList<IFastBuyPackageList>>('', { packageId });

      if (pkgs.items.length > 0) {
        pkg = pkgs.items[0];
      }
    }

    model.value.buyerId = pkg?.buyerId ?? null;
    model.value.sellerId = pkg?.sellerId ?? null;
  }
  emits('package', pkg);
}

const getSellerMoniker = async (sellerId: string): Promise<string> => {
  if (helper.value === undefined || helper.value === null) return '';
  try {
    window.loading?.('Loading Seller Info');
    const org = await helper.value.getData<IOrganization>(`organizations/${sellerId}`);
    return org.moniker;
  }catch (e: any) {
    instance?.proxy.$log('Failed to retrieve Organization info', e);
    if (e instanceof AjaxHelperException) {
      // TODO: This needs to be updated when we change error logging changes
      window.showAlert?.error(`Organization: ${e?.data || e.message}`);
    } else {
      window.showAlert?.error(`An error occurred`);
    }
  } finally {
    window.loading?.();
  }
  return '';
}


watch(() => model.value.sellerId, async (newSellerId: string | null) => {
  instance?.proxy.$log('Update SellerId', newSellerId);

  packageSource.value = new PackageSource(newSellerId, model.value.buyerId, props.hasLoans, props.status, model.value.packageId);
  emits('input', model.value);

  if (sellerSource.value.items.length > 0) {
    const seller = sellerSource.value.items.find(x => x.id === newSellerId) ?? null;
    if (seller && seller.associatedInternalEntity)
        model.value.buyerId = seller.associatedInternalEntity;
    emits('seller', seller);
  }
});

watch(() => sellerSource.value.isLoaded, (newIsLoaded: boolean) => {
  if (newIsLoaded && !isNullOrEmpty(model.value.sellerId)) {
    instance?.proxy.$log('Seller Loaded After Default Setter');
    const seller = sellerSource.value.find(x => x.id === model.value.sellerId);
    
    if (seller !== undefined)
      emits('seller', seller);
    else
      model.value.sellerId = null;
  }
});

watch(() => model.value.buyerId, async (newBuyerId: string | null) => {
  instance?.proxy.$log('Update BuyerId', newBuyerId);

  packageSource.value = new PackageSource(model.value.sellerId, newBuyerId, props.hasLoans, props.status, model.value.packageId);
  emits('input', model.value);
  sellerSource.value = new SellerSource(true, newBuyerId);

  if (buyerSource.value.items.length > 0) {
    const buyer = buyerSource.value.items.find(x => x.id === newBuyerId) ?? null;
    emits('buyer', buyer);
  }
});

watch(() => buyerSource.value.isLoaded, (newIsLoaded: boolean) => {
  if (newIsLoaded && !isNullOrEmpty(model.value.buyerId)) {
    instance?.proxy.$log('Buyer Loaded After Default Setter');
    const buyer = buyerSource.value.find(x => x.id === model.value.buyerId);
    if (buyer !== undefined) {
      emits('buyer', buyer);
    }
  }
});

watch(() => props.hasLoans, (newHasLoans: boolean | null) => {
  packageSource.value = new PackageSource(model.value.sellerId, model.value.buyerId, newHasLoans, props.status, model.value.packageId);
}); 

watch(() => model.value.packageId, async (newPackageId: string | null) => {
  instance?.proxy.$log('Update PackageId', newPackageId);

  emits('input', model.value);
  await setPackage(newPackageId);
});

watch(() => props.status, (newStatus: string) => {
  packageSource.value = new PackageSource(model.value.sellerId, model.value.buyerId, props.hasLoans, newStatus, model.value.packageId);
});

watch(() => props.value, (newValue: IPackageGroup) => {
  if (newValue !== undefined) {
    model.value = newValue;
  }
}, { immediate: true });

watch(() => props.verificationToken, (newVerificationToken: string) => {
  helper.value = new AjaxHelper(dataSourceUrl.value, newVerificationToken, null, null);
});

watch(() => user.value, (newUser: any) => {
  if (!newUser) {
    entitledToEditCounterparty.value = false;
    return;
  }
  entitledToEditCounterparty.value = newUser.hasEntitlement(Entitlements.VendorsContactsModule.EdiCounterparties);
}, { immediate: true });

onMounted(async () => {
  helper.value = new AjaxHelper(dataSourceUrl.value, props.verificationToken, null, null);

  if (model.value.buyerId === null && props.loadPreferences) {
    model.value.buyerId = await getDefaultBuyer();
  }

  packageSource.value = new PackageSource(model.value.sellerId, model.value.buyerId, props.hasLoans, props.status, model.value.packageId);
});

return { __sfc: true,instance, props, emits, dataSourceUrl, helper, model, buyerSource, sellerSource, packageSource, entitledToEditCounterparty, buyerLoading, sellerLoading, packageLoading, user, clearPackage, addNewBuyer, addNewSeller, addNewPackage, getDefaultBuyer, packageDataLoaded, setPackage, getSellerMoniker, Field, DropdownField, Action }
}

})